import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useRef } from "react";
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setTokenExpired } from '../../Redux/authSlice';
import authService from '../../_services/auth.service';
import wlTokenService from '../../_services/wlToken.service';
import { getCookie, removeCookie, setCookie } from "../../lib/cookies";
import {
  getWhitelabelFromMemory,
  removeCustomerFromMemory,
  setCustomerInMemory,
  setWhitelabelInMemory,
} from "../../lib/sharedSessionMemory";
import { persistSessionToFallback, clearSessionFallback } from '../../lib/sessionStorage';
import { useScreenTracking } from "../../hooks/useScreenTracking";
import { trackMarketplaceLogin } from "../../utils/gtm";
import { fetchWhitelabelByChannel } from "../../_services/whitelabelConfig.service";

const LOADING_GIF_URL = process.env.REACT_APP_LOADING_GIF_URL || "https://i.sstatic.net/kOnzy.gif";


const createLoaderInDOM = () => {
  const existingLoader = document.getElementById('auth-loader-container');
  if (existingLoader) {
    return existingLoader;
  }

  const loaderContainer = document.createElement('div');
  loaderContainer.id = 'auth-loader-container';
  loaderContainer.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: #f7f7f7;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    overflow: hidden;
  `;

  const preloader = document.createElement('div');
  preloader.style.cssText = `
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  `;

  const preloaderInner = document.createElement('div');
  preloaderInner.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const loaderImage = document.createElement('img');
  loaderImage.src = LOADING_GIF_URL;
  loaderImage.alt = 'Carregando...';
  loaderImage.style.cssText = `
    max-width: 200px;
    max-height: 200px;
    width: auto;
    height: auto;
  `;

  preloaderInner.appendChild(loaderImage);
  preloader.appendChild(preloaderInner);
  loaderContainer.appendChild(preloader);
  document.body.appendChild(loaderContainer);

  document.body.style.overflow = 'hidden';

  return loaderContainer;
};

const removeLoaderFromDOM = () => {
  const loader = document.getElementById('auth-loader-container');
  if (loader) {
    loader.remove();
  }
  document.body.style.overflow = '';
};

if (typeof document !== 'undefined') {
  if (window.location.pathname.includes('/auth') || window.location.search.includes('token=')) {
    if (document.body) {
      createLoaderInDOM();
    } else {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          createLoaderInDOM();
        });
      } else {
        createLoaderInDOM();
      }
    }
  }
}

export default function AuthRedirectComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authFlowStarted = useRef(false);
  const screenName = useScreenTracking();

  const isValidateChannel500 = (error) =>
    error?.response?.status === 500 &&
    String(error?.config?.url || "").includes("/jwt/validate-channel");

  const normalizeRedirectPath = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    if (/^https?:\/\//i.test(trimmed)) return null;

    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  };

  useEffect(() => {
    createLoaderInDOM();

    const runAuthFlow = async () => {
      if (authFlowStarted.current) return;
      authFlowStarted.current = true;
      try {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        const redirect = queryParams.get('redirect');
        
        if (!token) {
          removeLoaderFromDOM();
          window.location.href = '/';
          return;
        }

        setCookie('wlt', token);
        setCustomerInMemory({});

        const normalizedRedirect = normalizeRedirectPath(redirect);
        if (normalizedRedirect) localStorage.setItem('redirect_path', normalizedRedirect);

        const API_MONOLITO = process.env.REACT_APP_MONOLITO_URL;

        let channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          channel = payload?.channel ?? channel;
        } catch (err) {
          console.warn('Falha ao decodificar token:', err);
        }

        const authData = await authService.postValidateChannelDeduped();

        if (!authData?.success) {
          console.error('Validação de canal falhou');
          wlTokenService.clearTokens();
          removeCustomerFromMemory();
          removeLoaderFromDOM();
          navigate('/');
          return;
        }

        wlTokenService.saveTokens(authData, { accessToken: token });

        authService.saveOAuthFromValidateChannelResponse(authData);

        setCustomerInMemory(authData);
        dispatch(setTokenExpired(false));

        // Extrai whitelabel da resposta do validate-channel (mesma lógica do UserContext)
        let whitelabelData =
          authData?.whitelabel ||
          authData?.data?.whitelabel ||
          authData?.data?.config?.whitelabel ||
          null;

        if (whitelabelData) {
          setWhitelabelInMemory(whitelabelData);
        } else {
          // Fallback: tenta buscar via API de configurações
          try {
            whitelabelData = await fetchWhitelabelByChannel();
            if (whitelabelData) {
              setWhitelabelInMemory(whitelabelData);
            }
          } catch (whitelabelError) {
            const cachedWhitelabel = getWhitelabelFromMemory();
            if (cachedWhitelabel) {
              whitelabelData = cachedWhitelabel;
            }
          }
        }

        const entrypointUrl = `${API_MONOLITO}/${channel}/entrypoint?token=${encodeURIComponent(token)}`;

        try {
          await axios.get(entrypointUrl, { 
            withCredentials: true,
            timeout: 10000 
          });
        } catch (entrypointError) {
          console.error('Erro ao criar sessão:', entrypointError.message);
        }

        const redirectPath = normalizeRedirectPath(localStorage.getItem('redirect_path'));
        removeLoaderFromDOM();
        navigate(redirectPath || '/', { replace: true });
      } catch (err) {
        console.error('Erro no processo de autenticação:', err);
        wlTokenService.clearTokens();
        removeCustomerFromMemory();
        removeLoaderFromDOM();
        if (isValidateChannel500(err)) {
          dispatch(setTokenExpired(true));
          return;
        }
        navigate('/');
      }
    };

    runAuthFlow();
    return () => {
    };
  }, [location.search]);


  return null;
}
